import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import ChecklistDashboardTile from "@/components/ChecklistDashboardTile";
import { useAuth } from "@/context/AuthContext";
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Wallet,
  Download,
  TrendingUp,
  CalendarOff,
  ArrowRight,
  ClipboardList,
  AlertTriangle,
} from "lucide-react";

function StatCard({ label, value, sub, Icon, accent = "brand" }) {
  const ring =
    accent === "brand"
      ? "ring-red-100 bg-gradient-to-br from-white to-red-50/40"
      : accent === "emerald"
      ? "ring-emerald-100 bg-gradient-to-br from-white to-emerald-50/40"
      : accent === "amber"
      ? "ring-amber-100 bg-gradient-to-br from-white to-amber-50/40"
      : "ring-slate-100 bg-white";
  const iconBg =
    accent === "brand"
      ? "brand-bg text-white"
      : accent === "emerald"
      ? "bg-emerald-600 text-white"
      : accent === "amber"
      ? "bg-amber-500 text-white"
      : "bg-slate-200 text-slate-700";
  return (
    <div
      className={`rounded-xl ring-1 ${ring} p-5 flex items-start gap-4`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
        <div className="text-2xl font-bold text-slate-900 leading-none" style={{ fontFamily: "Chivo" }}>
          {value}
        </div>
        {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
      </div>
    </div>
  );
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const fmtMonthLabel = (m) => {
  if (!m) return "—";
  const d = new Date(typeof m === "string" && m.length === 7 ? `${m}-01` : m);
  if (isNaN(d.getTime())) return "—";
  return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
};

export default function PortalDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [delegation, setDelegation] = useState(null);
  const [smGrid, setSmGrid] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async (silent = false) => {
      try {
        const { data: d } = await api.get("/my-portal/dashboard");
        if (!cancelled) setData(d);
        try {
          const { data: ds } = await api.get("/delegation/stats");
          if (!cancelled) setDelegation(ds);
        } catch (e) { console.error("[PortalDashboard] delegation/stats failed", e); }
        // SM Weekly Checklist tile — only for senior_management
        if (user?.role === "senior_management") {
          try {
            const today = new Date();
            const wd = (today.getDay() + 6) % 7;
            const mon = new Date(today);
            mon.setDate(mon.getDate() - wd);
            const monIso = mon.toISOString().slice(0, 10);
            const { data: sm } = await api.get(
              `/sm-checklist/weekly?sm_id=${user.id}&week_start=${monIso}`,
            );
            if (!cancelled) setSmGrid(sm);
          } catch (e) { console.error("[PortalDashboard] sm-checklist/weekly failed", e); }
        }
      } catch (e) {
        if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    // Auto-refresh balances + attendance every 60s
    const intervalId = setInterval(() => fetchData(true), 60_000);
    // Also refresh whenever the tab regains focus
    const onFocus = () => fetchData(true);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading...</div>;
  if (!data) return <div className="text-sm text-slate-500">No data</div>;

  const att = data.attendance || {};
  const lev = data.leave_balance || {};
  const slip = data.latest_payslip;
  const emp = data.employee || {};

  const monthLabel = (() => {
    if (!data.month) return "";
    const d = new Date(data.month);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  })();

  const otHours = Math.round((att.ot_minutes || 0) / 6) / 10;

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Dashboard</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Namaste, {emp.name?.split(" ")[0] || "team"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {emp.punch_id ? `Punch ID: ${emp.punch_id} · ` : ""}{emp.designation || ""}
          </p>
        </div>
        <div className="text-sm text-slate-500">{monthLabel}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Attendance"
          value={`${att.attendance_pct ?? 0}%`}
          sub={`${att.present || 0}P · ${att.half || 0}H · ${att.absent || 0}A`}
          Icon={CheckCircle2}
          accent="emerald"
        />
        <StatCard
          label="OT this month"
          value={otHours !== 0 ? `${otHours} hrs` : "—"}
          sub={`${att.holiday || 0} holidays · ${att.off || 0} offs`}
          Icon={TrendingUp}
          accent="brand"
        />
        <StatCard
          label="Leave Balance"
          value={`EL ${Math.max(0, Number(lev.EL ?? 0))} · PL ${Math.max(0, Number(lev.PL ?? 0))}`}
          sub={`Used YTD: EL ${lev.EL_used_ytd ?? 0} · PL ${lev.PL_used_ytd ?? 0}`}
          Icon={CalendarOff}
          accent="amber"
        />
        <StatCard
          label="Last Payslip"
          value={slip ? formatINR(slip.net_salary) : "—"}
          sub={slip ? fmtMonthLabel(slip.month) : "No payslip yet"}
          Icon={Wallet}
          accent="brand"
        />
      </div>

      {/* Phase 6 — Compliance checklist tile (auto-hides if no data) */}
      <ChecklistDashboardTile />

      <section className="bg-white rounded-xl border border-red-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <CalendarDays className="h-4 w-4 brand-text" /> {monthLabel} Attendance
          </h2>
          <Link
            to="/my-portal/attendance"
            className="text-xs font-medium brand-text hover:underline inline-flex items-center gap-1"
            data-testid="portal-attendance-link"
          >
            View calendar <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
          <div className="rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-3">
            <div className="text-2xl font-bold text-emerald-700">{att.present || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Present</div>
          </div>
          <div className="rounded-md border border-amber-100 bg-amber-50/40 px-3 py-3">
            <div className="text-2xl font-bold text-amber-700">{att.half || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Half-day</div>
          </div>
          <div className="rounded-md border border-red-100 bg-red-50/40 px-3 py-3">
            <div className="text-2xl font-bold text-red-700">{att.absent || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Absent</div>
          </div>
          <div className="rounded-md border border-red-100 bg-red-50/40 px-3 py-3">
            <div className="text-2xl font-bold brand-text">{att.holiday || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Holiday</div>
          </div>
          <div className="rounded-md border border-slate-100 bg-slate-50/40 px-3 py-3">
            <div className="text-2xl font-bold text-slate-700">{att.off || 0}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Off</div>
          </div>
        </div>
        <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {att.days_logged ?? 0} of {att.days_in_month ?? 0} days logged · {att.days_remaining ?? 0} days remaining
        </div>
      </section>

      {smGrid && user?.role === "senior_management" && (() => {
        const today = new Date().toISOString().slice(0, 10);
        const dayIdx = smGrid.days.indexOf(today);
        const todayLogs = dayIdx >= 0
          ? smGrid.grid.map((r) => r.logs_by_day[dayIdx]).filter(Boolean)
          : [];
        const todayTotal = smGrid.grid.length;
        const todayDone = todayLogs.filter((l) => l.status === "done").length;
        const todayPending = todayTotal - todayLogs.length;
        return (
          <section className="bg-white rounded-xl border border-red-100 p-5" data-testid="sm-weekly-tile">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <ClipboardList className="h-4 w-4 brand-text" /> My Weekly SM Tasks
              </h2>
              <Link
                to="/my-portal/sm-checklist"
                className="text-xs font-medium brand-text hover:underline inline-flex items-center gap-1"
                data-testid="sm-weekly-tile-link"
              >
                Open grid <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-md border border-blue-100 bg-blue-50/40 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-blue-700 tabular-nums" data-testid="sm-today-pending">
                  {todayPending}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Today · Pending</div>
              </div>
              <div className="rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-emerald-700 tabular-nums" data-testid="sm-today-done">
                  {todayDone}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Today · Done</div>
              </div>
              <div className="rounded-md border border-red-100 bg-red-50/40 px-3 py-3 text-center">
                <div className="text-2xl font-bold brand-text tabular-nums" data-testid="sm-week-done">
                  {smGrid.stats.completed}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Week · Done</div>
              </div>
              <div className="rounded-md border border-slate-100 bg-slate-50/40 px-3 py-3 text-center">
                <div className="text-2xl font-bold text-slate-700 tabular-nums" data-testid="sm-week-compliance">
                  {smGrid.stats.compliance_pct}%
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                  Week · Compliance
                  <div className="text-[10px] normal-case tracking-normal text-slate-400">
                    {smGrid.stats.completed}/{smGrid.stats.total_slots} slots
                  </div>
                </div>
              </div>
            </div>
            {todayLogs.length === 0 && todayTotal > 0 && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  <b>No tasks logged today yet</b> ({todayTotal} pending). Open the grid to log start/end time per task.
                </div>
              </div>
            )}
          </section>
        );
      })()}

      {delegation?.assigned_to_me && (delegation.assigned_to_me.total > 0 || delegation.assigned_by_me) && (
        <section className="bg-white rounded-xl border border-red-100 p-5" data-testid="delegation-widget">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <ClipboardList className="h-4 w-4 brand-text" /> Delegated Tasks
            </h2>
            <Link
              to="/delegation/tasks"
              className="text-xs font-medium brand-text hover:underline inline-flex items-center gap-1"
              data-testid="delegation-widget-link"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-md border border-blue-100 bg-blue-50/40 px-3 py-3 text-center">
              <div className="text-2xl font-bold text-blue-700" data-testid="delegation-inbox-open">
                {delegation.assigned_to_me.open || 0}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Open · For me</div>
            </div>
            <div className="rounded-md border border-red-100 bg-red-50/40 px-3 py-3 text-center" data-testid="delegation-inbox-overdue">
              <div className="text-2xl font-bold text-red-700 flex items-center justify-center gap-1">
                {delegation.assigned_to_me.overdue > 0 && <AlertTriangle className="h-4 w-4" />}
                {delegation.assigned_to_me.overdue || 0}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Overdue</div>
            </div>
            {delegation.assigned_by_me ? (
              <>
                <div className="rounded-md border border-red-100 bg-red-50/40 px-3 py-3 text-center" data-testid="delegation-outbox-open">
                  <div className="text-2xl font-bold brand-text">{delegation.assigned_by_me.open || 0}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Open · Assigned by me</div>
                </div>
                <div className="rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-3 text-center" data-testid="delegation-outbox-completed">
                  <div className="text-2xl font-bold text-emerald-700">{delegation.assigned_by_me.completed || 0}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Completed · By me</div>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-md border border-slate-100 bg-slate-50/40 px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{delegation.assigned_to_me.completed || 0}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Completed</div>
                </div>
                <div className="rounded-md border border-slate-100 bg-slate-50/40 px-3 py-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">{delegation.assigned_to_me.total || 0}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Total</div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {slip && (
        <section className="bg-white rounded-xl border border-red-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
              <Wallet className="h-4 w-4 brand-text" /> Latest Payslip
            </h2>
            <Link to="/my-portal/salary" className="text-xs font-medium brand-text hover:underline inline-flex items-center gap-1">
              All payslips <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Month</div>
              <div className="text-base font-semibold text-slate-900 mt-0.5">
                {fmtMonthLabel(slip.month)}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Gross</div>
              <div className="text-base font-semibold text-slate-900 mt-0.5">{formatINR(slip.gross_salary)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Net Pay</div>
              <div className="text-base font-bold brand-text mt-0.5">{formatINR(slip.net_salary)}</div>
            </div>
            <div className="flex items-end">
              <a
                href={`/api/my-portal/payslips/${slip.id}/pdf`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md brand-bg brand-bg-hover text-white text-sm font-medium"
                data-testid="portal-latest-payslip-download"
              >
                <Download className="h-4 w-4" /> Download PDF
              </a>
            </div>
          </div>
        </section>
      )}
      {!slip && (
        <section className="bg-white rounded-xl border border-red-100 p-5 text-sm text-slate-500">
          <Wallet className="h-4 w-4 inline-block mr-2 brand-text" />
          No payslip generated yet. Once HR finalises your monthly payroll, it will appear here.
        </section>
      )}
    </div>
  );
}
